import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import Modal from '../../components/Modal';
import monitoringService from '../../services/monitoring.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime, formatMoney } from '../../lib/datetime';
import { colors, gradients } from '../../styles/theme';
import type {
  AlertDisposition,
  AlertScope,
  MonitoringAlert,
} from '../../types/monitoring';

/**
 * AML transaction-monitoring queue — the surface for the alerts the daily sweep
 * raises over the ledger (structuring / velocity).
 *
 * The alert record is append-only (WORM) and has no status: an alert is OPEN
 * until a terminal disposition is on file, exactly like a sanctions screening.
 * Adjudication is maker/checker — dismissing closes an AML signal with no
 * action, so it needs a second reviewer: one proposes, a DIFFERENT one confirms
 * (the API enforces it; the UI just explains it). Confirming an alert as
 * suspicious is single-actor — it relaxes nothing, and the manager then acts by
 * freezing the account and/or filing a SAR.
 */

const SEVERITY_TONE: Record<string, BadgeTone> = {
  high: 'danger',
  medium: 'warning',
  low: 'info',
};

const ALERT_TYPE_LABEL: Record<string, string> = {
  structuring: 'Structuring',
  velocity: 'Velocity',
};

/** What the reviewer is about to record, and how the dialog explains it. */
const DISPOSITION_COPY: Record<
  Exclude<AlertDisposition, 'dismissed'>,
  { title: string; blurb: string; cta: string }
> = {
  proposed_dismissal: {
    title: 'Propose dismissal',
    blurb:
      'Records your view that this activity is not suspicious. It does NOT close the alert — a different reviewer must confirm the dismissal (four-eyes).',
    cta: 'Propose dismissal',
  },
  confirmed_suspicious: {
    title: 'Confirm suspicious',
    blurb:
      'Closes the alert as a genuine AML signal. Act on it separately: freeze the account and/or file a SAR. Your reason is kept for the regulator.',
    cta: 'Confirm suspicious',
  },
};

function latestProposal(alert: MonitoringAlert): string | null {
  const proposal = [...alert.dispositions]
    .reverse()
    .find((d) => d.disposition === 'proposed_dismissal');
  return proposal ? proposal.reason : null;
}

interface RowActionsProps {
  alert: MonitoringAlert;
  onPropose: (a: MonitoringAlert) => void;
  onConfirmDismiss: (a: MonitoringAlert) => void;
  onConfirmSuspicious: (a: MonitoringAlert) => void;
}

/** Action cell for one alert. Extracted so the table map stays flat
 *  (Sonar S3776). A closed alert shows how it was resolved. */
function renderAlertActions(p: Readonly<RowActionsProps>): JSX.Element {
  const { alert: a } = p;
  if (!a.is_open) {
    const terminal = a.dispositions.find(
      (d) => d.disposition === 'dismissed' || d.disposition === 'confirmed_suspicious',
    );
    return (
      <span className="text-xs" style={{ color: colors.muted }}>
        {terminal?.disposition === 'dismissed' ? 'Dismissed' : 'Confirmed suspicious'}
      </span>
    );
  }
  const proposed = latestProposal(a) !== null;
  return (
    <span className="inline-flex items-center gap-2">
      {proposed ? (
        <button
          type="button"
          onClick={() => p.onConfirmDismiss(a)}
          className="px-3 py-1.5 rounded-md text-xs font-semibold"
          style={{ background: gradients.gold, color: colors.navy }}
        >
          Confirm dismissal
        </button>
      ) : (
        <button
          type="button"
          onClick={() => p.onPropose(a)}
          className="px-3 py-1.5 rounded-md text-xs font-semibold"
          style={{ border: `1px solid ${colors.border}`, color: colors.inkSoft }}
        >
          Propose dismissal
        </button>
      )}
      <button
        type="button"
        onClick={() => p.onConfirmSuspicious(a)}
        className="px-3 py-1.5 rounded-md text-xs font-semibold"
        style={{
          background: colors.dangerBg,
          color: colors.danger,
          border: `1px solid ${colors.dangerBorder}`,
        }}
      >
        Confirm suspicious
      </button>
    </span>
  );
}

interface DispositionTarget {
  alert: MonitoringAlert;
  disposition: AlertDisposition;
}

export default function ComplianceAlerts(): JSX.Element {
  const [scope, setScope] = useState<AlertScope>('open');
  const [alerts, setAlerts] = useState<MonitoringAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState<DispositionTarget | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setAlerts(await monitoringService.listAlerts(scope));
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load monitoring alerts.'));
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { reload(); }, [reload]);

  let body: JSX.Element;
  if (loading) {
    body = <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>;
  } else if (alerts.length === 0) {
    body = (
      <div
        className="rounded-2xl p-8 text-center text-sm"
        style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}
      >
        {scope === 'open'
          ? 'No open alerts — the monitoring queue is clear.'
          : 'No alerts to display.'}
      </div>
    );
  } else {
    body = (
      <div
        className="overflow-hidden rounded-2xl"
        style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
      >
        <table className="w-full text-sm">
          <thead style={{ background: colors.canvas, color: colors.muted }}>
            <tr>
              <th className="text-left font-medium px-4 py-3">Client</th>
              <th className="text-left font-medium px-4 py-3">Signal</th>
              <th className="text-right font-medium px-4 py-3">Total</th>
              <th className="text-left font-medium px-4 py-3">Detected</th>
              <th className="text-left font-medium px-4 py-3">Review</th>
              <th className="text-right font-medium px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {alerts.map((a, idx) => (
              <tr
                key={a.id}
                style={{ borderTop: idx === 0 ? 'none' : `1px solid ${colors.borderSoft}` }}
              >
                <td className="px-4 py-3" style={{ color: colors.ink }}>
                  {a.client_name}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <StatusBadge tone={SEVERITY_TONE[a.severity] ?? 'info'}>
                      {a.severity}
                    </StatusBadge>
                    <span style={{ color: colors.inkSoft }}>
                      {ALERT_TYPE_LABEL[a.alert_type] ?? a.alert_type}
                    </span>
                  </div>
                  <div className="text-xs mt-1" style={{ color: colors.muted }}>
                    {a.tx_count} transactions over {a.window_days} days
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-mono" style={{ color: colors.ink }}>
                  {formatMoney(a.total_amount, a.currency_code)}
                </td>
                <td className="px-4 py-3 text-xs" style={{ color: colors.muted }}>
                  {formatDateTime(a.detected_at)}
                </td>
                <td className="px-4 py-3 max-w-xs text-xs" style={{ color: colors.muted }}>
                  {a.dispositions.length === 0 ? (
                    <span style={{ color: colors.mutedSoft }}>Not reviewed</span>
                  ) : (
                    <span className="line-clamp-2">
                      {a.dispositions[a.dispositions.length - 1].reason}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {renderAlertActions({
                    alert: a,
                    onPropose: (x) => setTarget({ alert: x, disposition: 'proposed_dismissal' }),
                    onConfirmDismiss: (x) => setTarget({ alert: x, disposition: 'dismissed' }),
                    onConfirmSuspicious: (x) =>
                      setTarget({ alert: x, disposition: 'confirmed_suspicious' }),
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="AML monitoring"
        subtitle="Structuring and velocity signals raised over the ledger by the daily sweep. An alert stays open until it is adjudicated: dismissing one needs two distinct reviewers, confirming it suspicious closes it for follow-up (freeze and/or SAR)."
        actions={
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value as AlertScope)}
            className="px-3 py-1.5 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.border}`, background: colors.surface }}
          >
            <option value="open">Open only</option>
            <option value="closed">Adjudicated</option>
            <option value="all">All</option>
          </select>
        }
      />

      {flash && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{
            background: colors.successBg,
            border: `1px solid ${colors.successBorder}`,
            color: colors.successText,
          }}
        >
          {flash}
        </div>
      )}

      {error && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
        >
          {error}
        </div>
      )}

      {body}

      {target && (
        <DispositionModal
          target={target}
          onClose={() => setTarget(null)}
          onSubmitted={(msg) => {
            setTarget(null);
            setFlash(msg);
            globalThis.setTimeout(() => setFlash(null), 6000);
            reload();
          }}
        />
      )}
    </>
  );
}

interface DispositionModalProps {
  target: DispositionTarget;
  onClose: () => void;
  onSubmitted: (msg: string) => void;
}

function DispositionModal({
  target, onClose, onSubmitted,
}: Readonly<DispositionModalProps>): JSX.Element {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { alert: a, disposition } = target;
  const trimmed = reason.trim();
  const valid = trimmed.length >= 1 && trimmed.length <= 2000;

  const copy =
    disposition === 'dismissed'
      ? {
          title: 'Confirm dismissal',
          blurb:
            'Closes the alert as not suspicious. You cannot confirm a dismissal you proposed yourself — a different reviewer must.',
          cta: 'Confirm dismissal',
        }
      : DISPOSITION_COPY[disposition];

  const proposal = latestProposal(a);

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await monitoringService.disposition(a.id, disposition, trimmed);
      onSubmitted(
        res.alert_closed
          ? `Alert for ${a.client_name} closed.`
          : `Dismissal proposed for ${a.client_name} — a second reviewer must confirm it.`,
      );
    } catch (e: unknown) {
      setErr(getApiErrorMessage(e, 'Could not record the decision.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} busy={busy} maxWidth="lg">
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>
        {copy.title}
      </h2>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        {copy.blurb}
      </p>

      <div
        className="mb-4 p-3 rounded-lg text-xs"
        style={{ background: colors.surfaceMuted, color: colors.textPrimary }}
      >
        <div className="font-semibold">{a.client_name}</div>
        <div style={{ color: colors.muted }}>
          {ALERT_TYPE_LABEL[a.alert_type] ?? a.alert_type} · {a.severity} ·{' '}
          {a.tx_count} transactions over {a.window_days} days ·{' '}
          {formatMoney(a.total_amount, a.currency_code)}
        </div>
        {disposition === 'dismissed' && proposal && (
          <div className="mt-2 pt-2" style={{ borderTop: `1px solid ${colors.borderSoft}` }}>
            <span style={{ color: colors.muted }}>Proposed dismissal: </span>
            {proposal}
          </div>
        )}
      </div>

      <label htmlFor="alert-reason" className="block text-xs font-medium mb-1" style={{ color: colors.muted }}>
        Reason (kept in the audit log)
      </label>
      <textarea
        id="alert-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={4}
        className="w-full px-3 py-2 rounded-lg text-sm mb-4"
        style={{ border: `1px solid ${colors.border}` }}
      />

      {err && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
        >
          {err}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="px-4 py-2 rounded-lg text-sm"
          style={{ border: `1px solid ${colors.border}`, color: colors.inkSoft }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!valid || busy}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ background: gradients.gold, color: colors.navy, opacity: valid && !busy ? 1 : 0.5 }}
        >
          {busy ? 'Saving…' : copy.cta}
        </button>
      </div>
    </Modal>
  );
}
